// Stencil-based section caps: fill cut solid regions with a CAD-style hatch.
// After the main clipped render, each visible body is stencil-counted on its
// own (back IncrementWrap / front DecrementWrap). Per-body passes keep hollow
// solids and multi-body assemblies from cancelling each other in one shared
// stencil buffer. Where the count is non-zero we draw a hatch quad on the plane.
import * as THREE from 'three';
import {
  loadHatchFill,
  saveHatchFill,
  type HatchFillId
} from './preferences';
import {
  camera,
  getModelGroup,
  getModelRadius,
  onAfterRender,
  renderer,
  requestRender,
  scene
} from './viewer';

/** Soft CAD-friendly fills — muted enough for grey parts, distinct from each other. */
export const HATCH_FILL_PRESETS: Record<HatchFillId, { label: string; color: number }> = {
  steel: { label: 'Steel', color: 0x9eb6c8 },
  sand: { label: 'Sand', color: 0xd4c4a8 },
  slate: { label: 'Slate', color: 0xa8b0bc },
  mint: { label: 'Mint', color: 0xa8c9b8 },
  rose: { label: 'Rose', color: 0xd4a8a8 }
};

const HATCH_LINE_COLOR = new THREE.Color(0x2a2a2a);

let capsEnabled = false;
let registered = false;
let activePlane: THREE.Plane | null = null;
let currentFillId: HatchFillId = loadHatchFill();

const stencilBackMat = makeStencilMaterial(THREE.BackSide, THREE.IncrementWrapStencilOp);
const stencilFrontMat = makeStencilMaterial(THREE.FrontSide, THREE.DecrementWrapStencilOp);

/** Rewrites depth for the clipped solid so the hatch is occluded by geometry in front. */
const depthPrepassMat = new THREE.MeshBasicMaterial({
  toneMapped: false,
  colorWrite: false,
  depthWrite: true,
  depthTest: true,
  side: THREE.DoubleSide
});

const hatchScene = new THREE.Scene();
let hatchMesh: THREE.Mesh | null = null;
let hatchMaterial: THREE.ShaderMaterial | null = null;

const tmpU = new THREE.Vector3();
const tmpV = new THREE.Vector3();
const tmpN = new THREE.Vector3();
const tmpBitangent = new THREE.Vector3();
const zAxis = new THREE.Vector3(0, 0, 1);

function makeStencilMaterial(side: THREE.Side, zPass: THREE.StencilOp): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial({ toneMapped: false });
  mat.colorWrite = false;
  mat.depthWrite = false;
  mat.depthTest = false;
  mat.side = side;
  mat.stencilWrite = true;
  mat.stencilFunc = THREE.AlwaysStencilFunc;
  mat.stencilFail = zPass;
  mat.stencilZFail = zPass;
  mat.stencilZPass = zPass;
  return mat;
}

function fillColorFor(id: HatchFillId): THREE.Color {
  return new THREE.Color(HATCH_FILL_PRESETS[id].color);
}

function applyFillToMaterial(): void {
  if (!hatchMaterial) return;
  hatchMaterial.uniforms.uFillColor.value.copy(fillColorFor(currentFillId));
}

export function getHatchFillId(): HatchFillId {
  return currentFillId;
}

/** Change section-cap fill preset and persist the choice. */
export function setHatchFill(id: HatchFillId): void {
  currentFillId = id;
  saveHatchFill(id);
  applyFillToMaterial();
  requestRender();
}

function makeHatchMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uFillColor: { value: fillColorFor(currentFillId) },
      uHatchColor: { value: HATCH_LINE_COLOR.clone() },
      uPlanePoint: { value: new THREE.Vector3() },
      uPlaneU: { value: new THREE.Vector3(1, 0, 0) },
      uPlaneV: { value: new THREE.Vector3(0, 1, 0) },
      uHatchScale: { value: 2 }
    },
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorldPos = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uFillColor;
      uniform vec3 uHatchColor;
      uniform vec3 uPlanePoint;
      uniform vec3 uPlaneU;
      uniform vec3 uPlaneV;
      uniform float uHatchScale;
      varying vec3 vWorldPos;
      void main() {
        vec3 rel = vWorldPos - uPlanePoint;
        float u = dot(rel, uPlaneU);
        float v = dot(rel, uPlaneV);
        float diag = (u + v) / max(uHatchScale, 1e-4);
        // Dense, thin dark lines (~5% of period) with a slight antialias edge.
        float f = fract(diag);
        float line = 1.0 - smoothstep(0.0, 0.04, min(f, 1.0 - f));
        gl_FragColor = vec4(mix(uFillColor, uHatchColor, line), 1.0);
      }
    `,
    side: THREE.DoubleSide,
    // Occlude hatch behind solid faces (see depth prepass in renderSectionCaps).
    depthTest: true,
    depthWrite: false,
    // Slight pull toward camera avoids z-fight with coplanar cut faces, without
    // pulling through thick solids (large offsets caused "see-through" hatch).
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    // Custom ShaderMaterials skip clipping unless this is set; the cap sits on
    // the clip plane so it must not be sliced by the section plane itself.
    clipping: false,
    stencilWrite: true,
    stencilRef: 0,
    stencilFunc: THREE.NotEqualStencilFunc,
    stencilFail: THREE.ReplaceStencilOp,
    stencilZFail: THREE.ReplaceStencilOp,
    stencilZPass: THREE.ReplaceStencilOp
  });
}

function planeBasis(normal: THREE.Vector3, outU: THREE.Vector3, outV: THREE.Vector3): void {
  tmpN.copy(normal).normalize();
  const ax = Math.abs(tmpN.x), ay = Math.abs(tmpN.y), az = Math.abs(tmpN.z);
  if (ax < ay && ax < az) tmpBitangent.set(1, 0, 0);
  else if (ay < az) tmpBitangent.set(0, 1, 0);
  else tmpBitangent.set(0, 0, 1);
  outU.crossVectors(tmpN, tmpBitangent).normalize();
  outV.crossVectors(tmpN, outU).normalize();
}

function ensureHatchMesh(size: number): THREE.Mesh {
  if (hatchMesh) {
    const prev = hatchMesh.geometry as THREE.PlaneGeometry;
    const params = prev.parameters;
    if (params.width !== size || params.height !== size) {
      prev.dispose();
      hatchMesh.geometry = new THREE.PlaneGeometry(size, size);
    }
    return hatchMesh;
  }
  hatchMaterial = makeHatchMaterial();
  hatchMesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), hatchMaterial);
  hatchMesh.frustumCulled = false;
  hatchScene.add(hatchMesh);
  return hatchMesh;
}

export function updateSectionCaps(plane: THREE.Plane, center: THREE.Vector3): void {
  capsEnabled = true;
  activePlane = plane;
  ensureRegistered();

  // Local clipping on stencil / depth mats (same as the three.js stencil example).
  stencilBackMat.clippingPlanes = [plane];
  stencilFrontMat.clippingPlanes = [plane];
  depthPrepassMat.clippingPlanes = [plane];
  stencilBackMat.needsUpdate = true;
  stencilFrontMat.needsUpdate = true;
  depthPrepassMat.needsUpdate = true;

  const modelRadius = getModelRadius();
  const size = Math.max(modelRadius * 2.5, 1);
  const mesh = ensureHatchMesh(size);
  const n = plane.normal;
  const clipEpsilon = Math.max(modelRadius * 1e-4, 1e-3);
  mesh.position.copy(center).addScaledVector(n, clipEpsilon);
  mesh.quaternion.setFromUnitVectors(zAxis, n);

  if (hatchMaterial) {
    applyFillToMaterial();
    hatchMaterial.uniforms.uPlanePoint.value.copy(center);
    planeBasis(n, tmpU, tmpV);
    hatchMaterial.uniforms.uPlaneU.value.copy(tmpU);
    hatchMaterial.uniforms.uPlaneV.value.copy(tmpV);
    // Tighter spacing → more hatch lines across the cut.
    hatchMaterial.uniforms.uHatchScale.value = Math.max(modelRadius * 0.016, 0.25);
  }
}

export function clearSectionCaps(): void {
  capsEnabled = false;
  activePlane = null;
  stencilBackMat.clippingPlanes = null;
  stencilFrontMat.clippingPlanes = null;
  depthPrepassMat.clippingPlanes = null;
}

function ensureRegistered(): void {
  if (registered) return;
  registered = true;
  onAfterRender(renderSectionCaps);
}

function hideNonModelChildren(): Array<{ obj: THREE.Object3D; visible: boolean }> {
  const restored: Array<{ obj: THREE.Object3D; visible: boolean }> = [];
  const model = getModelGroup();
  for (const child of scene.children) {
    if (child === model) continue;
    restored.push({ obj: child, visible: child.visible });
    child.visible = false;
  }
  return restored;
}

const _bodyBox = new THREE.Box3();
const _corner = new THREE.Vector3();
const _viewSize = new THREE.Vector2();
const _scissor = new THREE.Vector4();

/**
 * Screen rectangle (CSS px, bottom-left origin) covering a body's bounds.
 *
 * The stencil clear and the hatch quad are both model-sized; scissoring them to
 * the body keeps a 200-part assembly from clearing and shading the whole canvas
 * 200 times per frame. Returns how the caller should proceed, because a body
 * straddling the camera plane cannot be projected safely.
 */
function bodyScissorRect(mesh: THREE.Mesh, out: THREE.Vector4): 'rect' | 'full' | 'offscreen' {
  const box = mesh.geometry.boundingBox;
  if (!box) return 'full';
  const perspective = (camera as THREE.PerspectiveCamera).isPerspectiveCamera === true;
  renderer.getSize(_viewSize);

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < 8; i++) {
    _corner.set(
      i & 1 ? box.max.x : box.min.x,
      i & 2 ? box.max.y : box.min.y,
      i & 4 ? box.max.z : box.min.z
    );
    _corner.applyMatrix4(mesh.matrixWorld).applyMatrix4(camera.matrixWorldInverse);
    // Behind (or on) the camera plane the perspective divide flips the point.
    if (perspective && _corner.z > -1e-4) return 'full';
    _corner.applyMatrix4(camera.projectionMatrix);
    if (_corner.x < minX) minX = _corner.x;
    if (_corner.x > maxX) maxX = _corner.x;
    if (_corner.y < minY) minY = _corner.y;
    if (_corner.y > maxY) maxY = _corner.y;
  }

  // One pixel of slack for the hatch's antialiased edge and its polygon offset.
  const x0 = Math.max(0, Math.floor((minX * 0.5 + 0.5) * _viewSize.x) - 1);
  const y0 = Math.max(0, Math.floor((minY * 0.5 + 0.5) * _viewSize.y) - 1);
  const x1 = Math.min(_viewSize.x, Math.ceil((maxX * 0.5 + 0.5) * _viewSize.x) + 1);
  const y1 = Math.min(_viewSize.y, Math.ceil((maxY * 0.5 + 0.5) * _viewSize.y) + 1);
  if (x1 <= x0 || y1 <= y0) return 'offscreen';

  out.set(x0, y0, x1 - x0, y1 - y0);
  return 'rect';
}

/**
 * Is this body a closed solid?
 *
 * A cap fills the inside of a cut solid, so it only means anything for a body
 * that has an inside. Counting back faces against front faces only cancels out
 * for watertight geometry; for anything else the whole silhouette stays marked
 * and the hatch covers the entire part instead of the cut. That is what turns a
 * wall into a solid block of hatching — and in architectural glTF, walls are
 * routinely single quads, double-sided shells or open boxes.
 *
 * A mesh is watertight when every edge is shared by exactly two triangles.
 * Edges are matched on vertex *position*, not index: glTF splits vertices per
 * face to get flat normals, so a perfectly closed box arrives with every corner
 * duplicated and index matching would call it open. The result is cached on the
 * geometry, so instanced parts pay for it once.
 */
function isClosedSolid(mesh: THREE.Mesh): boolean {
  const geometry = mesh.geometry;
  const cached = geometry.userData.capClosedSolid;
  if (typeof cached === 'boolean') return cached;

  const position = geometry.getAttribute('position');
  let closed = false;

  if (position && position.count > 0) {
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const box = geometry.boundingBox!;
    // Weld tolerance relative to the part, so it holds at any model scale.
    const span = Math.max(box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z, 1e-9);
    const step = span * 1e-5;

    const vertexId = new Map<string, number>();
    const weld = (i: number): number => {
      const key = `${Math.round(position.getX(i) / step)},${Math.round(position.getY(i) / step)},${Math.round(position.getZ(i) / step)}`;
      let id = vertexId.get(key);
      if (id === undefined) { id = vertexId.size; vertexId.set(key, id); }
      return id;
    };

    // Counted, not toggled: a double-sided shell has every edge four times,
    // which a parity check would happily call closed.
    const edgeUse = new Map<number, number>();
    const index = geometry.index;
    const count = index ? index.count : position.count;
    closed = count >= 12;   // fewer than four triangles cannot enclose anything

    for (let i = 0; i + 2 < count && closed; i += 3) {
      const a = weld(index ? index.getX(i) : i);
      const b = weld(index ? index.getX(i + 1) : i + 1);
      const c = weld(index ? index.getX(i + 2) : i + 2);
      if (a === b || b === c || a === c) continue;   // degenerate sliver
      for (const [u, v] of [[a, b], [b, c], [c, a]]) {
        const key = u < v ? u * 4194304 + v : v * 4194304 + u;
        const used = (edgeUse.get(key) ?? 0) + 1;
        if (used > 2) { closed = false; break; }
        edgeUse.set(key, used);
      }
    }
    if (closed) {
      for (const used of edgeUse.values()) {
        if (used !== 2) { closed = false; break; }
      }
    }
  }

  geometry.userData.capClosedSolid = closed;
  return closed;
}

/**
 * Visible bodies the plane actually cuts. Each body costs extra passes per
 * frame, so bodies entirely on one side of the plane are skipped, as are
 * surfaces that have no inside to fill.
 */
function collectCutBodies(model: THREE.Object3D, plane: THREE.Plane): THREE.Mesh[] {
  const bodies: THREE.Mesh[] = [];
  model.traverse(obj => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.userData.isModelBody || !mesh.visible) return;
    let p: THREE.Object3D | null = mesh;
    while (p) {
      if (!p.visible) return;
      p = p.parent;
    }
    const geometry = mesh.geometry;
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    _bodyBox.copy(geometry.boundingBox!).applyMatrix4(mesh.matrixWorld);
    if (!plane.intersectsBox(_bodyBox)) return;
    if (!isClosedSolid(mesh)) return;
    bodies.push(mesh);
  });
  return bodies;
}

/** Hide edge overlays and non-body meshes so they do not disturb the stencil. */
function hideStencilNoise(
  model: THREE.Object3D
): Array<{ obj: THREE.Object3D; visible: boolean }> {
  const restored: Array<{ obj: THREE.Object3D; visible: boolean }> = [];
  model.traverse(obj => {
    if (obj.userData.isModelBody) return;
    if (obj === model) return;
    // Keep assembly groups visible so body visibility still inherits correctly.
    if ((obj as THREE.Mesh).isMesh || (obj as THREE.LineSegments).isLineSegments) {
      restored.push({ obj, visible: obj.visible });
      obj.visible = false;
    }
  });
  return restored;
}

function renderSectionCaps(): void {
  if (!capsEnabled || !activePlane) return;
  const model = getModelGroup();
  if (!model || !hatchMesh || !hatchMaterial) return;

  const bodies = collectCutBodies(model, activePlane);
  if (bodies.length === 0) return;

  const prevAutoClear = renderer.autoClear;
  const prevOverride = scene.overrideMaterial;
  // A Color background forces a full clear inside renderer.render() even when
  // autoClear is false — that wiped our stencil between the back/front passes
  // and left the whole model silhouette marked.
  const prevBackground = scene.background;
  // Use local clippingPlanes on the stencil mats only (three.js example style);
  // avoid applying the same plane twice via renderer.clippingPlanes.
  const prevClippingPlanes = renderer.clippingPlanes;
  const restoredScene = hideNonModelChildren();
  const restoredNoise = hideStencilNoise(model);

  renderer.autoClear = false;
  scene.background = null;
  renderer.clippingPlanes = [];

  // Depth for the whole clipped model first so hatch is occluded correctly
  // across bodies, then stencil+hatch each body alone (hollow / multi-body safe).
  scene.overrideMaterial = depthPrepassMat;
  renderer.render(scene, camera);
  scene.overrideMaterial = prevOverride;

  for (const body of bodies) {
    const fit = bodyScissorRect(body, _scissor);
    if (fit === 'offscreen') continue;
    if (fit === 'rect') {
      renderer.setScissorTest(true);
      renderer.setScissor(_scissor.x, _scissor.y, _scissor.z, _scissor.w);
    } else {
      renderer.setScissorTest(false);
    }

    renderer.clear(false, false, true);

    // Render the body on its own rather than the whole scene with everything
    // else hidden: three.js walks, culls and sorts the entire graph per render,
    // so hiding 199 of 200 bodies still cost O(scene) three times per body.
    // Swapping the material does the same job in O(1).
    const bodyMaterial = body.material;
    body.material = stencilBackMat;
    renderer.render(body, camera);
    body.material = stencilFrontMat;
    renderer.render(body, camera);
    body.material = bodyMaterial;

    renderer.render(hatchScene, camera);
  }

  renderer.setScissorTest(false);
  for (const { obj, visible } of restoredNoise) obj.visible = visible;
  for (const { obj, visible } of restoredScene) obj.visible = visible;

  scene.overrideMaterial = prevOverride;
  scene.background = prevBackground;
  renderer.clippingPlanes = prevClippingPlanes;
  renderer.clear(false, false, true);
  renderer.autoClear = prevAutoClear;
}
